import { Trans, t } from '@lingui/macro';
import { EventEmitter } from "events";
import { PlusCircle, Pin, Pencil } from "lucide-react";
import { FC, useEffect, useState } from "react";
import { Room } from "../../../models/Room";
import { RoomItem } from "../../../models/RoomItem";
import { My } from "../../../services/My";
import { Util } from "../../../services/Utils/Util";
import MemoOpenModal from "./MemoOpenModal";
import { TextAssistantModalCtrl } from "./TextAssistantModal";
import { SectionCard, SectionActionButton, SectionListItem } from "./SectionCard";
import { Avatar, AvatarFallback, AvatarImage } from "../../../components/ui/avatar";
import { useToast } from "../../../hooks/use-toast";

export const MemoModalEmitter = new EventEmitter();
var LatestItems: RoomItem[] = [];

export const PanelMemos: FC<{ room: Room, items: RoomItem[] }> = ({ room, items }) => {
  const { toast } = useToast();
  const [selectedItem, setSelectedItem] = useState<RoomItem>();
  const [addText, setAddText] = useState<string | null>(null);

  useEffect(() => {
    MemoModalEmitter.on('view', (itemId: string) => {
      openSelectedItem(LatestItems.find(i => i.id === itemId))
    });
    MemoModalEmitter.on('create', (text: string) => {
      createMemo(text?.replaceAll('\n', '<br/>'));
    });
    MemoModalEmitter.on('add', (itemId: string, text: string) => {
      const item = LatestItems.find(i => i.id === itemId)
      if (item) {
        setAddText(text);
        setSelectedItem(item);
      } else {
        createMemo(text?.replaceAll('\n', '<br/>'));
        setTimeout(() => {
          toast({ title: t`Error`, description: t`Could not open the memo. Created a new instead.`, duration: 2000 });
        }, 1000);
      }
    });

    MemoModalEmitter.on('text-assistant', (text: string) => {
      TextAssistantModalCtrl.open(room, Util.getTextAsPlain(text), LatestItems);
    });

    return () => {
      MemoModalEmitter.removeAllListeners();
    }
  }, [room.id])

  useEffect(() => {
    LatestItems = items;
    if (selectedItem) {
      const item = items.find(i => i.id === selectedItem.id);
      if (item) {
        setSelectedItem(item);
      }
    }
  }, [items])

  return <>
    <SectionCard
      title={t`Memos`}
      collapsible={false}
      actions={
        <SectionActionButton
          onClick={() => createMemo()}
          icon={<PlusCircle className="w-4 h-4" />}
          label={t`Added memos`}
        />
      }
    >
      {items.length > 0 ? (
        <div className="flex flex-col">
          {items.map((i) => (
            <SectionListItem
              key={i.id}
              onClick={() => openSelectedItem(i)}
              className="group"
            >
              {/* 아이콘 영역 */}
              <div className="flex items-center gap-1 text-gray-400">
                {i.pinned && <Pin className="w-3.5 h-3.5 text-orange-500" />}
                {i.lockedByUserId && <Pencil className="w-3.5 h-3.5 text-blue-500" />}
              </div>

              {/* 메모 제목 */}
              <span className="flex-1 text-sm text-gray-900 truncate group-hover:text-orange-600 transition-colors">
                {i.title || t`Untitled memo`}
              </span>

              {/* 작성자 */}
              <div className="flex items-center gap-1.5 shrink-0">
                <Avatar className="w-5 h-5">
                  <AvatarImage src={i.user.photoUrl ?? Util.ANONYMOUS_PHOTO_URL} />
                  <AvatarFallback className="text-[10px]">
                    {i.user.name?.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                <span className="text-xs text-gray-500 max-w-[60px] truncate">
                  {i.user.name}
                </span>
              </div>
            </SectionListItem>
          ))}
        </div>
      ) : (
        <p className="text-sm text-gray-400 italic py-3 text-center">
          <Trans>No memos yet</Trans>
        </p>
      )}
    </SectionCard>

    {selectedItem && <MemoOpenModal item={selectedItem} addText={addText} onClose={() => setSelectedItem(undefined)} />}
  </>

  function openSelectedItem(i: RoomItem | undefined) {
    if (i) {
      setAddText(null);
      setSelectedItem(i);
    }
  }

  function createMemo(text: string = '') {
    const item = new RoomItem(room.id, My.user);
    item.type = 'memo';
    item.text = text;
    item.editableBy = 'everyone';
    item.id = ''; // Tells MemoOpenModal that the item is not saved yet.
    setAddText(null);
    setSelectedItem(item);
  }
}
